import { flow, types, getEnv } from "mobx-state-tree";
import { when } from "mobx";

import { BatchAnalysisStatusItem } from "./BatchAnalysisStatusItem";
import { api } from "../api/ApiService";
import { endpoints } from "../api/ApiEndpoints";
import { PubSubEvents } from "../../services/PubSubEvents";
import { BatchProcessingStatusModel } from "./BatchProcessingStatusModel";
import {
  BatchAnalysisStatus,
  BatchProcessingInspectionOption,
} from "../../generated/VideoAnalysis";

const BatchAnalysisStatusModel = BatchProcessingStatusModel.named("BatchAnalysisStatusModel")
  .actions((self) => ({
    finalizeBatchProcessing() {
      self.displaySnackbar = false;

      if (!self.displayStatusModal) {
        self.displayFinalizedModal = true;
      }
    },
  }))
  .actions((self) => {
    return {
      addInstanceResultStorage(currentInstanceItems: BatchAnalysisStatusItem[], index: number) {
        if (!self.instancesResultsStorages[index]) {
          self.instancesResultsStorages.push({
            autoLoad: false,
            useGridFiltering: false,
            noPagination: true,
            gridColumns: batchVideoAnalysisService.statusModel.tableColumns,
          });
        }

        self.instancesResultsStorages[index].setData({
          items: currentInstanceItems,
        });
      },

      updateBatchResults(response: any) {
        self.addBatchVideoAnalysisInstance({
          startedAt: new Date(),
          projectName: response.data[0].projectName,
          videoIds: response.data.map((x: any) => x.videoId),
        });

        const summary = response.data.map((x: any) => new BatchAnalysisStatusItem(x));

        if (!self.isProcessing) {
          var videoConversionToken = PubSub.subscribe(
            PubSubEvents.VideoConversionFinished,
            (_event: string, notification: Notification) => {
              const status = notification.data.success
                ? BatchAnalysisStatus.Triggered
                : BatchAnalysisStatus.Failed;
              batchVideoAnalysisService.statusModel.setStatus(notification.data.fileId, status);
            }
          );

          var videoAnalysisToken = PubSub.subscribe(
            PubSubEvents.VideoAnalysisFinished,
            (_event: string, notification: Notification) => {
              const status = notification.data.success
                ? BatchAnalysisStatus.Successfully
                : BatchAnalysisStatus.Failed;
              batchVideoAnalysisService.statusModel.setStatus(notification.data.fileId, status);
            }
          );

          when(
            () => !self.isProcessing,
            () => {
              PubSub.unsubscribe(videoConversionToken);
              PubSub.unsubscribe(videoAnalysisToken);
              batchVideoAnalysisService.statusModel.finalizeBatchProcessing();
            }
          );
        }

        self.batchResultsStorage.setData({
          items: self.batchResultsStorage.data?.items
            ? [...self.batchResultsStorage.data.items, ...summary]
            : summary,
        });

        self.showStatusBatchModal();
      },

      setStatus(videoId: number, status: BatchAnalysisStatus) {
        const index = self.batchResultsStorage.data.items.findIndex(
          (i: BatchAnalysisStatusItem) => i.videoId === videoId
        );
        if (index === -1) return;

        self.batchResultsStorage.data.items[index].id *= -1;
        self.batchResultsStorage.data.items[index].status = status;
        self.batchResultsStorage.setData({
          items: [...self.batchResultsStorage.data.items],
        });
      },
    };
  });

const BatchVideoAnalysisServiceModel = types
  .model("BatchVideoAnalysisServiceModel", {
    statusModel: BatchAnalysisStatusModel,
    displayModal: false,
    objectIds: types.array(types.string),
    runAnalysisOption: BatchProcessingInspectionOption.RelevantInspections,
    redoExistingAnalysis: false,
    startingWithDate: types.maybeNull(types.Date),
    batchSummary: types.maybeNull(
      types.model({
        objectsCount: types.number,
        inspectionsCount: types.number,
        analysingInspectionsCount: types.number,
        videoAnalysisCount: types.number,
      })
    ),
  })
  .views((self) => ({
    get totalCalls() {
      return self.batchSummary?.videoAnalysisCount ?? 0;
    },
    get isLoading() {
      return self.batchSummary === null;
    },
    get isStartingWithDateNeeded() {
      return (
        self.runAnalysisOption === BatchProcessingInspectionOption.StartingWithDate &&
        !self.startingWithDate
      );
    },
  }))
  .views((self) => ({
    get disabledRunAnalysis() {
      return self.totalCalls === 0 || self.objectIds.length === 0;
    },
  }))
  .actions((self) => {
    const loadBatchSummary = flow(function* () {
      self.batchSummary = null;
      if (self.isStartingWithDateNeeded) return;

      const endpoint = endpoints.get("summarize_batch_analysis");
      const response = yield getEnv(self).api.post(endpoint, {
        analysisOption: self.runAnalysisOption,
        redoExistingAnalysis: self.redoExistingAnalysis,
        startingWithDate: self.startingWithDate,
        objectIds: self.objectIds,
      });

      self.batchSummary = {
        objectsCount: response.data.objectsCount,
        inspectionsCount: response.data.inspectionsCount,
        analysingInspectionsCount: response.data.analysingInspectionsCount,
        videoAnalysisCount: response.data.videoAnalysisCount,
      };
    });

    return {
      showRunBatchModal(objectIds: string[]) {
        self.objectIds.replace(objectIds);
        self.runAnalysisOption = BatchProcessingInspectionOption.RelevantInspections;
        self.redoExistingAnalysis = false;
        self.startingWithDate = null;
        loadBatchSummary();
        self.displayModal = true;
      },
      hideRunBatchModal() {
        self.displayModal = false;
        self.objectIds.clear();
      },
      handleRunOptionChanged(option: number) {
        self.runAnalysisOption = option;
        loadBatchSummary();
      },
      handleRedoExistingAnalysisChanged(option: boolean) {
        self.redoExistingAnalysis = option;
        loadBatchSummary();
      },
      changeStartingWithDate(date: Date | null) {
        self.startingWithDate = date;
        if (self.runAnalysisOption === BatchProcessingInspectionOption.StartingWithDate)
          loadBatchSummary();
      },
      triggerBatchAnalysis: flow(function* () {
        if (self.isStartingWithDateNeeded) return;

        const data = {
          analysisOption: self.runAnalysisOption,
          redoExistingAnalysis: self.redoExistingAnalysis,
          startingWithDate: self.startingWithDate,
          objectIds: [...self.objectIds],
        };
        self.objectIds.clear();

        const endpoint = endpoints.get("trigger_batch_analysis");
        const response = yield getEnv(self).api.post(endpoint, data);

        self.displayModal = false;
        self.statusModel.updateBatchResults(response);
      }),
    };
  });

export const batchVideoAnalysisService = BatchVideoAnalysisServiceModel.create(
  {
    statusModel: {
      failedStatuses: [{ status: BatchAnalysisStatus.Failed.toString() }],
      batchResultsStorage: {
        autoLoad: false,
        useGridFiltering: false,
        noPagination: true,
      },
    },
    batchSummary: null,
  },
  { api }
);
